import { HashSet } from "./HashSet";
import { ObjUtils } from "./ObjUtils";
import { StringUtils } from "./StringUtils";
import { ValueEncoder } from "./ValueEncoder";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";

export enum ScheduleType {
	None,
	Seconds_Interval,
	Minutes_Interval,
	Hours_Interval,
	Days_Interval,
	Weekly_SelectedDays,
	Monthly_NthDay,
	Monthly_OrdinalDay,
	Yearly_NthDayOfMonth,
	Yearly_OrdinalDayOfMonth,
};


export enum ScheduleOrdinal {
	First,
	Second,
	Third,
	Fourth,
	Fifth,
	Last,
};


export enum ScheduleDayOfWeek {
	Sun,
	Mon,
	Tue,
	Wed,
	Thu,
	Fri,
	Sat,
};


export enum ScheduleMonth {
	Jan,
	Feb,
	Mar,
	Apr,
	May,
	Jun,
	Jul,
	Aug,
	Sep,
	Oct,
	Nov,
	Dec,
};


export class Schedule implements ObjectSerializer {

	public static readonly LastDayOfMonth = 0;

	public Type: ScheduleType;
	public Intervals: HashSet<number>;
	public Ordinals: HashSet<ScheduleOrdinal>;
	public StartTimes: HashSet<Date>;
	public DaysOfWeek: HashSet<ScheduleDayOfWeek>;
	public DaysOfMonth: HashSet<number>;
	public Months: HashSet<ScheduleMonth>;

	constructor() {
		this.Type = ScheduleType.None;
		this.Intervals = new HashSet<number>();
		this.Ordinals = new HashSet<ScheduleOrdinal>();
		this.StartTimes = new HashSet<Date>();
		this.DaysOfWeek = new HashSet<ScheduleDayOfWeek>();
		this.DaysOfMonth = new HashSet<number>();
		this.Months = new HashSet<ScheduleMonth>();
	}

	public get Summary(): string {

		let summary = "";
		let startTimes = "";
		let weeklyDays = "";
		let minutesAfter = "";

		let intervals = this.IntervalsAsString;

		switch (this.Type) {
			case ScheduleType.None:
				summary = "None";
				break;

			case ScheduleType.Days_Interval:
				startTimes = this.StartTimesAsString;
				if (!startTimes)
					summary = `Every ${intervals} days`;
				else
					summary = `Every ${intervals} days; Starting at [${startTimes}]`;
				break;

			case ScheduleType.Hours_Interval:
				minutesAfter = this.MinutesAfterString;
				if (!minutesAfter)
					summary = `Every ${intervals} hours`;
				else
					summary = `Every ${intervals} hours; Starting ${minutesAfter} Minute(s) after the hour`;
				break;

			case ScheduleType.Minutes_Interval:
				summary = `Every ${intervals} minutes`;
				break;
			case ScheduleType.Seconds_Interval:
				summary = `Every ${intervals} seconds`;
				break;

			case ScheduleType.Weekly_SelectedDays:
				weeklyDays = this.WeeklyDaysAsString;
				startTimes = this.StartTimesAsString;

				if (!startTimes)
					summary = `Every [${weeklyDays}]`;
				else
					summary = `Every [${weeklyDays}]; Starting at [${startTimes}]`;
				break;

			case ScheduleType.Monthly_OrdinalDay:
				summary = `Every [${ScheduleOrdinal[this.OneOrdinal]} - ${ScheduleDayOfWeek[this.OneDayOfWeek]}] of each month; Starting at [${this.StartTimesAsString}]`;
				break;
			case ScheduleType.Monthly_NthDay:
				summary = `Monthly on every [${this.DaysOfMonthString}]`;
				break;

			case ScheduleType.Yearly_OrdinalDayOfMonth:
				break;
			case ScheduleType.Yearly_NthDayOfMonth:
				break;
		}

		return summary;
	}

	public Copy(other: Schedule): void {
		this.Type = other.Type;
		this.Intervals = new HashSet<number>(other.Intervals);
		this.Ordinals = new HashSet<ScheduleOrdinal>(other.Ordinals);
		this.StartTimes = new HashSet<Date>(other.StartTimes);
		this.DaysOfWeek = new HashSet<ScheduleDayOfWeek>(other.DaysOfWeek);
		this.DaysOfMonth = new HashSet<number>(other.DaysOfMonth);
		this.Months = new HashSet<ScheduleMonth>(other.Months);
	}

	public static CreateSecondsInterval(seconds: number): Schedule {
		console.assert(seconds > 0);

		let newSchedule = new Schedule();
		newSchedule.Type = ScheduleType.Seconds_Interval;
		newSchedule.Intervals.Add(seconds);

		return newSchedule;
	}

	public static CreateMinutesInterval(minutes: number): Schedule {
		console.assert(minutes > 0);

		let newSchedule = new Schedule();
		newSchedule.Type = ScheduleType.Minutes_Interval;
		newSchedule.Intervals.Add(minutes);

		return newSchedule;
	}

	public static CreateDaysInterval(days: number): Schedule {
		console.assert(days > 0);

		let newSchedule = new Schedule();
		newSchedule.Type = ScheduleType.Days_Interval;
		newSchedule.OneInterval = days;

		let now = new Date();
		let startTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
		newSchedule.OneStartTime = startTime;

		return newSchedule;
	}

	public Equals(obj: Schedule): boolean {
		if (!obj)
			return false;

		if (this.Type !== obj.Type)
			return false;
		if (!ObjUtils.SetEquals(this.Intervals, obj.Intervals))
			return false;
		if (!ObjUtils.SetEquals(this.Ordinals, obj.Ordinals))
			return false;
		if (!ObjUtils.SetEquals(this.StartTimes, obj.StartTimes))
			return false;
		if (!ObjUtils.SetEquals(this.DaysOfWeek, obj.DaysOfWeek))
			return false;
		if (!ObjUtils.SetEquals(this.DaysOfMonth, obj.DaysOfMonth))
			return false;
		if (!ObjUtils.SetEquals(this.Months, obj.Months))
			return false;

		return true;
	}

	public get HasType(): boolean {
		return (this.Type !== ScheduleType.None);
	}

	public get HasIntervals(): boolean {
		return (this.Intervals.Count > 0);
	}

	public get HasOneInterval(): boolean {
		return (this.Intervals.Count == 1);
	}

	public get HasOrdinals(): boolean {
		return (this.Ordinals.Count > 0);
	}

	public get HasOneOrdinal(): boolean {
		return (this.Ordinals.Count == 1);
	}

	public get HasStartTimes(): boolean {
		return (this.StartTimes.Count > 0);
	}

	public get HasOneStartTime(): boolean {
		return (this.StartTimes.Count == 1);
	}

	public get HasDaysOfWeek(): boolean {
		return (this.DaysOfWeek.Count > 0);
	}

	public get HasOneDayOfWeek(): boolean {
		return (this.DaysOfWeek.Count == 1);
	}

	public get HasDaysOfMonth(): boolean {
		return (this.DaysOfMonth.Count > 0);
	}

	public get HasOneDayOfMonth(): boolean {
		return (this.DaysOfMonth.Count == 1);
	}

	public get HasMonths(): boolean {
		return (this.Months.Count > 0);
	}

	public get HasOneMonth(): boolean {
		return (this.Months.Count == 1);
	}

	public get OneInterval(): number {
		console.assert(this.HasOneInterval);
		return this.Intervals.First();
	}
	public set OneInterval(value: number) {
		this.ClearIntervals();
		this.AddInterval(value);
	}

	public get OneOrdinal(): ScheduleOrdinal {
		console.assert(this.HasOneOrdinal);
		return this.Ordinals.First();
	}
	public set OneOrdinal(value: ScheduleOrdinal) {
		this.ClearOrdinals();
		this.AddOrdinal(value);
	}

	public get OneStartTime(): Date {
		console.assert(this.HasOneStartTime);
		return this.StartTimes.First();
	}
	public set OneStartTime(value: Date) {
		this.ClearStartTimes();
		this.AddStartTime(value);
	}

	public get OneDayOfWeek(): ScheduleDayOfWeek {
		console.assert(this.HasOneDayOfWeek);
		return this.DaysOfWeek.First();
	}
	public set OneDayOfWeek(value: ScheduleDayOfWeek) {
		this.ClearDaysOfWeek();
		this.AddDayOfWeek(value);
	}

	public get OneDayOfMonth(): number {
		console.assert(this.HasOneDayOfMonth);
		return this.DaysOfMonth.First();
	}
	public set OneDayOfMonth(value: number)	{
		this.ClearDaysOfMonth();
		this.AddDayOfMonth(value);
	}

	public get OneMonth(): ScheduleMonth {
		console.assert(this.HasOneMonth);
		return this.Months.First();
	}
	public set OneMonth(value: ScheduleMonth) {
		this.ClearMonths();
		this.AddMonth(value);
	}

	public Clear(): void {
		this.Type = ScheduleType.None;

		this.ClearIntervals();
		this.ClearOrdinals();
		this.ClearStartTimes();
		this.ClearDaysOfWeek();
		this.ClearDaysOfMonth();
		this.ClearMonths();
	}

	public ClearIntervals(): void {
		this.Intervals.Clear();
	}

	public ClearOrdinals(): void {
		this.Ordinals.Clear();
	}

	public ClearStartTimes(): void {
		this.StartTimes.Clear();
	}

	public ClearDaysOfWeek(): void {
		this.DaysOfWeek.Clear();
	}

	public ClearDaysOfMonth(): void {
		this.DaysOfMonth.Clear();
	}

	public ClearMonths(): void {
		this.Months.Clear();
	}

	public AddInterval(value: number): void {
		console.assert(value > 0);
		this.Intervals.Add(value);
	}

	public AddOrdinal(value: ScheduleOrdinal): void {
		this.Ordinals.Add(value);
	}

	public AddStartTime(value: Date): void {
		this.StartTimes.Add(value);
	}

	public AddDayOfWeek(value: ScheduleDayOfWeek): void {
		this.DaysOfWeek.Add(value);
	}

	public AddDayOfMonth(value: number): void {
		console.assert((1 <= value && value <= 31) || (value === Schedule.LastDayOfMonth));
		this.DaysOfMonth.Add(value);
	}

	public AddMonth(value: ScheduleMonth): void {
		this.Months.Add(value);
	}

	private get IntervalsAsString(): string	{
		let intervals = "";
		if (this.HasIntervals) {
			this.Intervals.ForEach((value: number) => {
				if (intervals.length > 0)
					intervals += ", ";
				intervals += value;
			});
		}
		return intervals;
	}

	private get MinutesAfterString(): string {
		let minutesAfter = "";
		if (this.HasOneStartTime) {
			let totalMinutes = this.StartTimes.First().getMinutes();
			if (totalMinutes > 0)
				minutesAfter = totalMinutes.toString();
		}
		return minutesAfter;
	}

	private get StartTimesAsString(): string {
		let startTimes = "";
		if (this.HasStartTimes) {
			this.StartTimes.ForEach((dateTime: Date) => {
				if (startTimes.length > 0)
					startTimes += ", ";
				startTimes += dateTime.toLocaleTimeString();
			});
		}
		return startTimes;
	}

	private get WeeklyDaysAsString(): string {
		let weeklyDays = "";
		if (this.HasDaysOfWeek) {
			this.DaysOfWeek.ForEach((day: ScheduleDayOfWeek) => {
				if (weeklyDays.length > 0)
					weeklyDays += ", ";
				weeklyDays += ScheduleDayOfWeek[day];
			});
		}
		return weeklyDays;
	}

	private get DaysOfMonthString(): string {
		let monthlyDays = "";
		if (this.HasDaysOfMonth) {
			this.DaysOfMonth.ForEach((day: number) => {
				if (monthlyDays.length > 0)
					monthlyDays += ", ";
				monthlyDays += day;
			});
		}
		return monthlyDays;
	}

	public Write(obj: any, context: any): void {
		obj.schedule = {};
		obj = obj.schedule;

		if (this.HasType)
			obj.type = ScheduleType[this.Type];

		if (this.HasIntervals)
			this.WriteSetElem<number>(obj, "intervals", this.Intervals, ((value: number) => {
				return ValueEncoder.EncodeInteger(value);
			}));

		if (this.HasOrdinals)
			this.WriteSetElem<ScheduleOrdinal>(obj, "ordinals", this.Ordinals, ((value: ScheduleOrdinal) => {
				return ScheduleOrdinal[value];
			}));

		if (this.HasStartTimes)
			this.WriteSetElem<Date>(obj, "start_times", this.StartTimes, ((value: Date) => {
				return ValueEncoder.EncodeDateTime(value);
			}));

		if (this.HasDaysOfWeek)
			this.WriteSetElem<ScheduleDayOfWeek>(obj, "days_of_week", this.DaysOfWeek, ((value: ScheduleDayOfWeek) => {
				return ScheduleDayOfWeek[value];
			}));

		if (this.HasDaysOfMonth)
			this.WriteSetElem<number>(obj, "days_of_month", this.DaysOfMonth, ((value: number) => {
				return ValueEncoder.EncodeInteger(value);
			}));

		if (this.HasMonths)
			this.WriteSetElem<ScheduleMonth>(obj, "months", this.Months, ((value: ScheduleMonth) => {
				return ScheduleMonth[value];
			}));
	}

	public Read(obj: any, context: any): void {
		this.Clear();

		if (!obj.schedule)
			throw new Error();
		obj = obj.schedule;

		if (obj.type !== undefined)
			this.Type = ScheduleType[<string>obj.type];

		if (obj.intervals !== undefined)
			this.ReadSetElem(obj, "intervals", this.Intervals, ((value: string) => {
				return ValueEncoder.DecodeInteger(value);
			}));

		if (obj.ordinals !== undefined)
			this.ReadSetElem(obj, "ordinals", this.Ordinals, ((value: string) => {
				return ScheduleOrdinal[value];
			}));

		if (obj.start_times !== undefined)
			this.ReadSetElem(obj, "start_times", this.StartTimes, ((value: string) => {
				return ValueEncoder.DecodeDateTime(value);
			}));

		if (obj.days_of_week !== undefined)
			this.ReadSetElem(obj, "days_of_week", this.DaysOfWeek, ((value: string) => {
				return ScheduleDayOfWeek[value];
			}));

		if (obj.days_of_month !== undefined)
			this.ReadSetElem(obj, "days_of_month", this.DaysOfMonth, ((value: string) => {
				return ValueEncoder.DecodeInteger(value);
			}));

		if (obj.months !== undefined)
			this.ReadSetElem(obj, "months", this.Months, ((value: string) => {
				return ScheduleMonth[value];
			}));
	}

	private WriteSetElem<T>(obj: any, elemName: string, set: HashSet<T>,
		valueEncoder: (value: T) => string): void {

		let csv: string = StringUtils.SetToCsv(set, valueEncoder);
		obj[elemName] = csv;
	}

	private ReadSetElem<T>(obj: any, elemName: string, set: HashSet<T>,
		valueDecoder: (value: string) => T): void {

		let csv: string = obj[elemName];
		StringUtils.CsvToSet(csv, set, valueDecoder);
	}

}
